import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUserPromotions, useDeletePromotion, formatPromotionTitle, sortPromotionsByHierarchy } from '@/features/profile/hooks/usePromotions';
import { UserPromotion } from '@/types/promotions';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/shared/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import EditIcon from '@/assets/icons/edit.svg';
import { format } from 'date-fns';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { AddPromotionModal } from './AddPromotionModal';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';

// Import belt SVGs
import whiteBelt from '@/assets/images/belts/white_belt.svg';
import blueBelt from '@/assets/images/belts/blue_belt.svg';
import purpleBelt from '@/assets/images/belts/purple_belt.svg';
import brownBelt from '@/assets/images/belts/brown_belt.svg';
import blackBelt from '@/assets/images/belts/black_belt.svg';
import defaultBelt from '@/assets/images/belts/default_belt.svg';

// Import belt with stripes SVGs
import whiteBelt1Stripe from '@/assets/images/belts/white_belt_1_stripes.svg';
import whiteBelt2Stripes from '@/assets/images/belts/white_belt_2_stripes.svg';
import whiteBelt3Stripes from '@/assets/images/belts/white_belt_3_stripes.svg';
import whiteBelt4Stripes from '@/assets/images/belts/white_belt_4_stripes.svg';
import blueBelt1Stripe from '@/assets/images/belts/blue_belt_1_stripes.svg';
import blueBelt2Stripes from '@/assets/images/belts/blue_belt_2_stripes.svg';
import blueBelt3Stripes from '@/assets/images/belts/blue_belt_3_stripes.svg';
import blueBelt4Stripes from '@/assets/images/belts/blue_belt_4_stripes.svg';
import purpleBelt1Stripe from '@/assets/images/belts/purple_belt_1_stripes.svg';
import purpleBelt2Stripes from '@/assets/images/belts/purple_belt_2_stripes.svg';
import purpleBelt3Stripes from '@/assets/images/belts/purple_belt_3_stripes.svg';
import purpleBelt4Stripes from '@/assets/images/belts/purple_belt_4_stripes.svg';
import brownBelt1Stripe from '@/assets/images/belts/brown_belt_1_stripes.svg';
import brownBelt2Stripes from '@/assets/images/belts/brown_belt_2_stripes.svg';
import brownBelt3Stripes from '@/assets/images/belts/brown_belt_3_stripes.svg';
import brownBelt4Stripes from '@/assets/images/belts/brown_belt_4_stripes.svg';
import blackBelt1Stripe from '@/assets/images/belts/black_belt_1_stripes.svg';
import blackBelt2Stripes from '@/assets/images/belts/black_belt_2_stripes.svg';
import blackBelt3Stripes from '@/assets/images/belts/black_belt_3_stripes.svg';
import blackBelt4Stripes from '@/assets/images/belts/black_belt_4_stripes.svg';

// Belt icon mapping
const beltIcons: Record<string, string> = {
  white: whiteBelt,
  blue: blueBelt,
  purple: purpleBelt,
  brown: brownBelt,
  black: blackBelt,
  default: defaultBelt,
};

// Belt with stripes icon mapping
const beltStripesIcons: Record<string, Record<number, string>> = {
  white: {
    1: whiteBelt1Stripe,
    2: whiteBelt2Stripes,
    3: whiteBelt3Stripes,
    4: whiteBelt4Stripes,
  },
  blue: {
    1: blueBelt1Stripe,
    2: blueBelt2Stripes,
    3: blueBelt3Stripes,
    4: blueBelt4Stripes,
  },
  purple: {
    1: purpleBelt1Stripe,
    2: purpleBelt2Stripes,
    3: purpleBelt3Stripes,
    4: purpleBelt4Stripes,
  },
  brown: {
    1: brownBelt1Stripe,
    2: brownBelt2Stripes,
    3: brownBelt3Stripes,
    4: brownBelt4Stripes,
  },
  black: {
    1: blackBelt1Stripe,
    2: blackBelt2Stripes,
    3: blackBelt3Stripes,
    4: blackBelt4Stripes,
  },
};

interface MyJourneyTabProps {
  userId: string;
  isOwnProfile: boolean;
}

export const MyJourneyTab: React.FC<MyJourneyTabProps> = ({ userId, isOwnProfile }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const [promotionToDelete, setPromotionToDelete] = useState<UserPromotion | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [promotionToEdit, setPromotionToEdit] = useState<UserPromotion | null>(null);

  const { data: promotions = [], isLoading, error } = useUserPromotions(userId);
  const deletePromotionMutation = useDeletePromotion();

  // Fetch belt_rank scoped to userId (not user.id) so it works for own profile
  // AND for viewing other users' profiles. Uses same queryKey invalidated by mutations.
  const { data: profileData } = useQuery({
    queryKey: ['user-profile', userId],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('belt_rank')
        .eq('id', userId)
        .single();
      return data;
    },
    enabled: !!userId,
  });
  const currentBeltRank = profileData?.belt_rank ?? 'white';

  // Merge virtual current belt with actual promotions
  const allPromotions = React.useMemo(() => {
    if (!currentBeltRank) return promotions;
    
    // Create virtual promotion inline to avoid recreation on every render
    const virtualCurrentBelt: UserPromotion = {
      id: 'virtual-current-belt',
      user_id: userId,
      belt_color: currentBeltRank as any,
      stripe_count: 0,
      graduation_date: undefined,
      instructor_name: undefined,
      school_name: undefined,
      created_at: '2025-01-01T00:00:00.000Z', // Static date to prevent recreation
      updated_at: '2025-01-01T00:00:00.000Z', // Static date to prevent recreation
    };
    
    // Check if we already have a promotion for current belt with 0 stripes
    const hasCurrentBeltPromotion = promotions.some(
      p => p.belt_color === currentBeltRank && p.stripe_count === 0
    );
    
    if (hasCurrentBeltPromotion) {
      return promotions;
    }
    
    return [virtualCurrentBelt, ...promotions];
  }, [promotions, currentBeltRank, userId]);

  // Sort promotions by hierarchy - memoized to prevent recalculation
  const sortedPromotions = React.useMemo(() => {
    return sortPromotionsByHierarchy(allPromotions);
  }, [allPromotions]);

  const handleDeletePromotion = React.useCallback((promotion: UserPromotion) => {
    setPromotionToDelete(promotion);
    setShowDeleteDialog(true);
  }, []);

  const confirmDelete = React.useCallback(async () => {
    if (!promotionToDelete) return;

    try {
      await deletePromotionMutation.mutateAsync(promotionToDelete.id);
      // Success - the mutation's onSuccess will handle query invalidation
      toast({
        title: t('common.success'),
        description: t('profile.promotion_deleted_successfully'),
        duration: 2000,
      });
    } catch (error) {
      // Only show error toast if there's actually an error
      console.error('Error deleting promotion:', error);
      toast({
        title: t('common.error'),
        description: t('profile.error_deleting_promotion'),
        variant: "destructive",
      });
    }
  }, [promotionToDelete, deletePromotionMutation, toast, t]);

  const handleAddPromotion = React.useCallback(() => {
    setPromotionToEdit(null);
    setShowAddModal(true);
  }, []);

  const handleEditPromotion = React.useCallback((promotion: UserPromotion) => {
    // Virtual promotions will be converted to real promotions when editing
    setPromotionToEdit(promotion);
    setShowAddModal(true);
  }, []);

  const getBeltIcon = React.useCallback((promotion: UserPromotion) => {
    const { belt_color, stripe_count } = promotion;
    
    if (stripe_count === 0) {
      // Belt promotion - return belt icon
      return beltIcons[belt_color] || beltIcons.default;
    } else {
      // Stripe promotion - return belt with stripes icon
      const beltStripes = beltStripesIcons[belt_color];
      if (beltStripes && beltStripes[stripe_count]) {
        return beltStripes[stripe_count];
      }
      // Fallback to default belt if specific stripe count doesn't exist
      return beltIcons.default;
    }
  }, []);

  const handleImageError = React.useCallback((event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // Fallback to default belt icon if the specific one doesn't exist
    event.currentTarget.src = beltIcons.default;
  }, []);

  const formatDate = React.useCallback((dateString?: string) => {
    if (!dateString) return null;
    
    // Parse the date string directly to avoid timezone issues
    // graduation_date is a DATE type (no time), so we parse it as-is
    let year: number, month: number, day: number;
    
    // Handle different date formats
    if (dateString.includes('T')) {
      // ISO string with time (e.g., "2025-12-02T00:00:00.000Z")
      const date = new Date(dateString);
      // Use UTC methods to avoid timezone conversion
      year = date.getUTCFullYear();
      month = date.getUTCMonth() + 1;
      day = date.getUTCDate();
    } else if (dateString.includes('-')) {
      // Date string format (e.g., "2025-12-02")
      const parts = dateString.split('-');
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    } else {
      // Fallback to Date object parsing
      const date = new Date(dateString);
      year = date.getFullYear();
      month = date.getMonth() + 1;
      day = date.getDate();
    }
    
    if (t('common.language') === 'pt') {
      return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    } else {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[month - 1]} ${day}, ${year}`;
    }
  }, [t]);

  const formatPromotionTitle = React.useCallback((promotion: UserPromotion) => {
    if (promotion.stripe_count === 0) {
      return t('profile.promotions.belt_text', { belt: t(`profile.belt_colors.${promotion.belt_color}`) });
    }
    
    const stripeText = promotion.stripe_count === 1 ? '1st' : 
                      promotion.stripe_count === 2 ? '2nd' : 
                      promotion.stripe_count === 3 ? '3rd' : '4th';
    
    return t('profile.promotions.stripe_text', { 
      stripe: t(`profile.promotions.stripe.${stripeText}`), 
      belt: t(`profile.belt_colors.${promotion.belt_color}`) 
    });
  }, [t]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-lg text-white">{t('profile.my_journey.loading_promotions')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-lg text-red-400">{t('profile.my_journey.error_loading_promotions')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-3 mt-6 -mx-3">
      {/* Add new promotion button - only visible on own profile */}
      {isOwnProfile && (
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={handleAddPromotion}
        >
          <Plus className="w-4 h-4" />
          {t('profile.my_journey.add_new_promotion')}
        </Button>
      )}

      {/* Timeline */}
      <div className="relative">
        {/* Timeline line using CSS Grid for perfect alignment */}
        {/* Use flex-direction: column-reverse to display from black (top) to white (bottom) */}
        <div className="flex flex-col-reverse gap-8">
          {/* Promotions - each in its own row, modular with its own segment */}
          {/* Order is natural (white -> black), but CSS reverses visual display */}
          {sortedPromotions.length > 0 ? (
            sortedPromotions.map((promotion, index) => {
              const isLast = index === sortedPromotions.length - 1;
              
              // Get segment color based on CURRENT promotion's belt color (where segment originates)
              // This promotion renders its own segment that goes to the next promotion
              const currentBeltColor = promotion.belt_color;
              
              // Debug: log to verify we're using the correct promotion
              // console.log(`Promotion ${index}: belt=${currentBeltColor}, id=${promotion.id}`);
              
              const getSegmentColor = (beltColor: string) => {
                switch (beltColor) {
                  case 'white': return 'bg-white';
                  case 'blue': return 'bg-blue-500';
                  case 'purple': return 'bg-purple-500';
                  case 'brown': return 'bg-amber-700';
                  case 'black': return 'bg-black';
                  default: return 'bg-gray-500';
                }
              };
              
              // Segment color is determined by the CURRENT promotion (this one)
              // IMPORTANT: This is the promotion that RENDERS the segment, not the next one
              const segmentColor = getSegmentColor(currentBeltColor);
              
              return (
                <div key={promotion.id} className="relative grid grid-cols-[auto_1fr] gap-3 items-start flex-shrink-0">
                  {/* Belt icon with perfect centering */}
                  <div className="relative flex-shrink-0">
                    {/* Timeline marker - perfectly centered on the line */}
                    <div className="absolute left-1/2 top-1/2 w-4 h-4 bg-gray-600 rounded-full border-2 border-gray-800 -translate-x-1/2 -translate-y-1/2 z-10"></div>
                    
                    {/* Belt icon container */}
                    <div className="relative w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden z-20">
                      <img
                        src={getBeltIcon(promotion)}
                        alt={`${promotion.belt_color} belt`}
                        className="w-full h-full object-cover"
                        onError={handleImageError}
                      />
                    </div>
                    
                    {/* Segment line - modular, rendered by each promotion */}
                    {/* This segment originates from THIS promotion and goes to the next one */}
                    {/* Color is determined by THIS promotion's belt color */}
                    {/* With flex-col-reverse, the segment goes UP (negative top) to reach the next promotion above */}
                    {!isLast && (
                      <div 
                        className={`absolute left-1/2 top-1/2 w-0.5 ${segmentColor} -translate-x-1/2 z-0`}
                        style={{
                          // With flex-col-reverse, next promotion is ABOVE, so segment goes UP
                          // Height = icon height (56px) + gap (32px) = 88px to reach center of next icon above
                          height: '88px',
                          // Position starts at center of icon, but goes UP (negative top)
                          top: '50%',
                          transform: 'translateX(-50%) translateY(-100%)',
                        }}
                      />
                    )}
                  </div>

                {/* Promotion details */}
                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-white mb-1 truncate">
                        {promotion.id === 'virtual-current-belt' 
                          ? t('profile.promotions.belt_text', { belt: t(`profile.belt_colors.${currentBeltRank}`) })
                          : formatPromotionTitle(promotion)
                        }
                      </h3>
                      
                      <div className="space-y-0.5 text-sm">
                        {/* Graduation date - always show */}
                        <div className="flex items-center">
                          <span className="text-gray-400 font-medium text-xs whitespace-nowrap">{t('profile.my_journey.graduation_date')}</span>
                          <span className="text-white font-normal text-xs ml-1 whitespace-nowrap">
                            {promotion.graduation_date ? formatDate(promotion.graduation_date) : t('profile.my_journey.not_informed')}
                          </span>
                        </div>
                        
                        {/* Only show instructor for own profile */}
                        {isOwnProfile && (
                          <div className="flex items-center">
                            <span className="text-gray-400 font-medium text-xs whitespace-nowrap">{t('profile.my_journey.instructor')}</span>
                            <span className="text-white font-normal text-xs ml-1 whitespace-nowrap">
                              {promotion.instructor_name || t('profile.my_journey.not_informed')}
                            </span>
                          </div>
                        )}
                        
                        {/* Only show school for own profile */}
                        {isOwnProfile && (
                          <div className="flex items-center">
                            <span className="text-gray-400 font-medium text-xs whitespace-nowrap">{t('profile.my_journey.school')}</span>
                            <span className="text-white font-normal text-xs ml-1 whitespace-nowrap">
                              {promotion.school_name || t('profile.my_journey.not_informed')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Action buttons - only for own profile */}
                    {isOwnProfile && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Show edit button for all promotions (virtual can be converted to real) */}
                        <button
                          onClick={() => handleEditPromotion(promotion)}
                          className="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                          title={t('common.edit')}
                        >
                          <img src={EditIcon} alt={t('common.edit')} width={16} height={16} />
                        </button>
                        {/* Only show delete button for real promotions (not virtual) */}
                        {promotion.id !== 'virtual-current-belt' && (
                          <button
                            onClick={() => handleDeletePromotion(promotion)}
                            className="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                            title={t('common.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <p className="text-white/60">{t('profile.my_journey.no_promotions_found')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white border-gray-300 rounded-2xl w-[80vw] max-w-none">
          {/* Custom Header with Close Button */}
          <div className="flex items-center justify-between mb-4 pt-4">
            <div className="flex-1" />
            <AlertDialogTitle className="text-xl font-semibold text-black leading-none tracking-normal">
              {t('profile.my_journey.delete_promotion')}
            </AlertDialogTitle>
            <div className="flex-1 flex justify-end">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none border-0 focus:ring-0 focus:border-0"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="sr-only">{t('profile.my_journey.close')}</span>
              </button>
            </div>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 text-center">
              {t('profile.my_journey.delete_promotion_confirmation')}
            </p>

            <div className="flex gap-3 pt-4">
              <AlertDialogCancel className="flex-1 h-12 text-black border-black hover:bg-gray-100 rounded-full flex items-center justify-center">
                {t('profile.my_journey.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="flex-1 h-12 bg-sossiety-red hover:bg-sossiety-red-hover text-white rounded-full flex items-center justify-center"
              >
                {t('profile.my_journey.delete')}
              </AlertDialogAction>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Promotion Modal */}
      <AddPromotionModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setPromotionToEdit(null);
        }}
        promotionToEdit={promotionToEdit}
      />
    </div>
  );
}; 